/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { action, computed } from '@ember/object';
import DeviceAttributeCondition, {
  getSpecialDeviceAttributeConfig,
  isDeviceExistsCondition,
  isSpecialDeviceAttribute,
  SpecialDeviceAttributes,
} from 'cio/app-models/conditions/device-attribute';
import { alias } from '@ember/object/computed';
import EnvironmentService from 'cio/services/environment';
import { isBlank, isNone } from '@ember/utils';
import { limitedOperators } from 'cio/utils/conditions-operators';
import { ConditionEditorSubLayout } from 'cio/utils/condition-editor';
import ConditionEditorDeviceAttributeDeviceModelValue from 'cio/components/condition-editor/device-attribute/device-model-value';
import ConditionEditorDeviceAttributeValue from 'cio/components/condition-editor/device-attribute/value';

interface DeviceAttributeArgs {
  condition?: DeviceAttributeCondition;
  actionItems?: { value: string; label: string }[];
  attributesFilter?: (item: { label: string; value: string }) => boolean;
  customerItems?: { value: string; label: string }[];
  limitOperators: boolean;
  changeType?: () => unknown;
  onRemove?: () => unknown;
  variation?: 'compact' | 'segment';
  subLayout?: ConditionEditorSubLayout;
  disabled?: boolean;
}

type OptionItem = { value: string; label: string };

export default class DeviceAttribute extends Component<DeviceAttributeArgs> {
  @service environment!: EnvironmentService;

  placeholder = 'device attribute';

  @alias('environment.deviceAttributes') deviceAttributes!: string[];

  get attributeValues() {
    let { condition } = this.args;
    if (isNone(condition)) {
      return [];
    }
    return this.specialAttributeConfig!.values;
  }

  @computed('args.condition.name')
  get isDeviceModelCondition() {
    return this.args.condition?.name === SpecialDeviceAttributes.device_model;
  }

  get valueComponent() {
    return this.isDeviceModelCondition
      ? ConditionEditorDeviceAttributeDeviceModelValue
      : ConditionEditorDeviceAttributeValue;
  }

  get specialAttributes() {
    let specialAttributes: OptionItem[] = [];
    for (let value in SpecialDeviceAttributes) {
      let { label } = getSpecialDeviceAttributeConfig(value)!;
      specialAttributes.push({ label, value });
    }
    return specialAttributes;
  }

  @computed('args.condition.name')
  get specialAttributeConfig() {
    let { condition } = this.args;
    if (isNone(condition) || !isSpecialDeviceAttribute(condition.name)) {
      return {
        defaultOperator: 'eq',
        defaultValue: '',
        label: '',
        operators: [],
        values: [],
      };
    }
    return getSpecialDeviceAttributeConfig(condition.name);
  }

  @computed('deviceAttributes.[]')
  get attributeNames() {
    let customAttributes = (this.deviceAttributes || [])
      .filter(this.isNotSpecialAttribute)
      .map((name: string) => ({ label: name, value: name }));
    let noFilter = () => true;
    let attributeFilter = this.args.attributesFilter ?? noFilter;
    return [...this.specialAttributes, ...customAttributes].filter(attributeFilter);
  }

  private isNotSpecialAttribute(name: string): boolean {
    return !isSpecialDeviceAttribute(name);
  }

  @computed('args.condition.name')
  get operators() {
    let { condition } = this.args;
    if (isNone(condition)) {
      return [];
    }

    if (!this.isSpecialDeviceAttribute && this.useOnlyLimitedOperators) {
      return limitedOperators;
    }
    // this attribute is expected to be used only by special attributes
    // but this is safe.
    return this.specialAttributeConfig!.operators;
  }

  get useOnlyLimitedOperators() {
    return this.args.limitOperators ?? false;
  }

  @computed('args.condition.name')
  get isDeviceExistsCondition() {
    let { condition } = this.args;
    return isNone(condition) ? false : isDeviceExistsCondition(condition.name);
  }

  @computed('args.condition.name')
  get isSpecialDeviceAttribute() {
    let { condition } = this.args;
    return isNone(condition) ? false : isSpecialDeviceAttribute(condition.name);
  }

  get useDeviceConditionEditor() {
    return !this.isLastUsedCondition && this.isSpecialDeviceAttribute;
  }

  get useCustomOperatorComponent() {
    return this.isLastUsedCondition || this.useOnlyLimitedOperators;
  }

  @computed('args.condition.name')
  get isLastUsedCondition() {
    return this.args.condition?.name === SpecialDeviceAttributes._last_used;
  }

  @action
  onNameChange(name: string) {
    let { condition } = this.args;
    if (isBlank(name) || isNone(condition)) {
      return;
    }
    let operator = 'eq';
    let value = '';
    if (isSpecialDeviceAttribute(name)) {
      let config = getSpecialDeviceAttributeConfig(name);
      operator = config?.defaultOperator ?? 'eq';
      value = config?.defaultValue ?? '';
    }
    this.args.condition?.setProperties({ name, operator, value });
  }
}
